import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, Calendar, Tag, ArrowRight } from 'lucide-react';
import { parseBilingualContent } from './BlogPost';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content?: string;
  excerpt: string;
  tags: string[];
  published_at: string;
  created_at: string;
}

const Blog: React.FC = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeLang, setActiveLang] = useState<'en' | 'zh'>(
    typeof window !== 'undefined' && window.location.hash === '#zh' ? 'zh' : 'en'
  );

  const switchLang = (lang: 'en' | 'zh') => {
    setActiveLang(lang);
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', lang === 'zh' ? '#zh' : window.location.pathname);
    }
  };

  // Mock data for demonstration
  const mockPosts: BlogPost[] = [
    {
      id: '1',
      title: 'The Future of Digital Publishing',
      slug: 'future-digital-publishing',
      excerpt: 'Exploring how traditional publishing is evolving with digital technologies and what this means for authors and readers.',
      tags: ['publishing', 'technology', 'innovation'],
      published_at: '2024-01-15',
      created_at: '2024-01-15'
    },
    {
      id: '2',
      title: 'Educational Technology Trends 2024',
      slug: 'edtech-trends-2024',
      excerpt: 'A comprehensive look at the latest trends in educational technology and how they\'re shaping the future of learning.',
      tags: ['education', 'technology', 'trends'],
      published_at: '2024-01-10',
      created_at: '2024-01-10'
    },
    {
      id: '3',
      title: 'Building Interactive Learning Applications',
      slug: 'interactive-learning-apps',
      excerpt: 'Best practices for creating engaging educational applications that enhance learning outcomes.',
      tags: ['app-development', 'education', 'learning'],
      published_at: '2024-01-05',
      created_at: '2024-01-05'
    },
    {
      id: '4',
      title: 'Woodlogos Company Updates',
      slug: 'woodlogos-company-updates',
      excerpt: 'Latest news and updates from Woodlogos, including new projects and company milestones.',
      tags: ['company', 'updates', 'news'],
      published_at: '2024-01-01',
      created_at: '2024-01-01'
    }
  ];


  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/blog');
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            setPosts(result.data);
          } else {
            // Try localStorage first (for local development)
            const localPosts = getLocalPosts();
            setPosts(localPosts.length > 0 ? localPosts : []);
          }
        } else {
          // Fallback to localStorage if API fails
          const localPosts = getLocalPosts();
          setPosts(localPosts.length > 0 ? localPosts : []);
        }
      } catch (error) {
        console.error('Failed to fetch posts:', error);
        // Fallback to localStorage if API fails
        const localPosts = getLocalPosts();
        setPosts(localPosts.length > 0 ? localPosts : []);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  // Helper function to get posts from localStorage (for local development)
  const getLocalPosts = () => {
    try {
      const stored = localStorage.getItem('blog_posts');
      if (stored) {
        const allPosts = JSON.parse(stored);
        // Only return published posts and transform format
        return allPosts
          .filter((post: any) => post.published === 1 || post.published === true)
          .map((post: any) => ({
            ...post,
            tags: typeof post.tags === 'string' ? JSON.parse(post.tags) : post.tags,
            created_at: post.created_at || post.published_at || new Date().toISOString()
          }));
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return [];
  };


  const formatDate = (dateString: string, lang: 'en' | 'zh' = 'en') => {
    return new Date(dateString).toLocaleDateString(lang === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading blog posts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Helmet>
        <html lang={activeLang === 'zh' ? 'zh-CN' : 'en'} />
        <title>{activeLang === 'zh' ? '每月观察 | Woodlogos' : 'Monthly Notes | Woodlogos'}</title>
        <meta name="description" content="Monthly Notes from Woodlogos: what we noticed this month in technology, education, publishing and the space between China and the US." />
        <link rel="canonical" href="https://www.woodlogos.net/blog" />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-50 to-blue-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center fade-in">
            <div className="w-20 h-20 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <FileText className="h-10 w-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {activeLang === 'zh' ? '每月观察' : 'Monthly Notes'}
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              {activeLang === 'zh'
                ? '这个月我们看到了什么：科技、教育、出版，以及中美之间。'
                : 'What we noticed this month: technology, education, publishing and the space between China and the US.'}
            </p>
            <div className="flex items-center justify-center gap-2">
              {(['en', 'zh'] as const).map(lang => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => switchLang(lang)}
                  aria-pressed={activeLang === lang}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeLang === lang
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {lang === 'zh' ? '中文' : 'English'}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Blog Posts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {posts.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No posts found
              </h3>
              <p className="text-gray-600">
                No blog posts available at the moment.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post, index) => {
                const bilingual = post.content ? parseBilingualContent(post.content) : null;
                const lang: 'en' | 'zh' = bilingual ? activeLang : 'en';
                const englishTags: string[] = (typeof post.tags === 'string' ? JSON.parse(post.tags) : post.tags) || [];
                const shown = lang === 'zh' && bilingual
                  ? {
                      title: bilingual.zhTitle || post.title,
                      excerpt: bilingual.zhExcerpt || post.excerpt,
                      tags: bilingual.zhTags.length ? bilingual.zhTags : englishTags,
                    }
                  : { title: post.title, excerpt: post.excerpt, tags: englishTags };
                return (
                <article 
                  key={post.id}
                  className={`relative bg-white rounded-xl shadow-lg overflow-hidden card-hover ${
                    index === 0 ? 'slide-in-left' : 
                    index === 1 ? 'fade-in' : 'slide-in-right'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex items-center text-sm text-gray-500 mb-3">
                      <Calendar className="h-4 w-4 mr-1" />
                      <span>{formatDate(post.published_at, lang)}</span>
                    </div>
                    
                    <h2 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                      {shown.title}
                    </h2>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3">
                      {shown.excerpt}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(() => {
                        const tags = shown.tags;
                        return (Array.isArray(tags) ? tags : []).map(tag => (
                          <span 
                            key={tag}
                            className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium"
                          >
                            <Tag className="h-3 w-3 inline mr-1" />
                            {tag}
                          </span>
                        ));
                      })()}
                    </div>
                    
                    <Link 
                      to={`/blog/${post.slug}${lang === 'zh' ? '#zh' : ''}`}
                      className="text-purple-600 font-medium hover:text-purple-700 flex items-center after:absolute after:inset-0 after:content-['']"
                    >
                      {lang === 'zh' ? '阅读全文' : 'Read More'}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Link>
                  </div>
                </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Blog;
